<template>
	<div id='cashflow-calculator' v-on:mouseleave="showHelpModal=true" ref="cashflow-calculator">
		<div class="calculator-steps">
		<img id="print-logo" class="show-print" src="https://soundproperty.com.au/wp-content/uploads/2019/03/Cashflow-Calculator-New-2018.png"/>
		<transition name="step">
		<purchase-costs id="purchase-costs" class="show-print" v-show="currentStepIndex == 0"></purchase-costs>
		</transition>
		<transition name="step">
			<income-and-expenses id="income-and-expenses" class="show-print" v-show="currentStepIndex == 1"></income-and-expenses>
		</transition>
		<transition name="step">
		<tax-factors id="tax-factors" class="show-print" v-show="currentStepIndex == 2"></tax-factors>
		</transition>
		<transition name="step">
		<results id="results" v-show="currentStepIndex == 3"></results>
		</transition>
		<step-button :current-step-index="currentStepIndex" :back-step-title="stepTitles[currentStepIndex - 1]" :next-step-title="stepTitles[currentStepIndex + 1]" id="step-button" class="hide-print" ref="stepButton" v-on:backStep="previousStepChange(); viewToTop()" v-on:nextStep="nextStepChange(); viewToTop()"></step-button>
        <previous-step-button :current-step-index="currentStepIndex" :back-step-title="stepTitles[currentStepIndex - 1]" v-show="currentStepIndex != 0" id="previous-step-button" class="hide-print" v-on:backStep="previousStepChange(); viewToTop()" v-on:nextStep="nextStepChange(); viewToTop()"></previous-step-button>
        <next-step-button :current-step-index="currentStepIndex" :next-step-title="stepTitles[currentStepIndex + 1]" v-show="currentStepIndex != 3" id="next-step-button" class="hide-print" v-on:backStep="previousStepChange(); viewToTop()" v-on:nextStep="nextStepChange(); viewToTop()"></next-step-button>
		</div>
		<div class="calculator-info hide-print">
			<p class="calculator-description">
				The Sound Property Cashflow Calculator is a user-friendly tool
				for residential property investors, designed to give a quick estimate
				of the net operating in the first year of ownership.
				The Sound Property Cashflow Calculator combines the rental revenue
				and operating expenses of the property, with the percentage of
				income tax paid, to measure the net change in the investor's
				weekly and annual income.
			</p>

			<h4 class="calculator-instruction-label">How to use this calculator:</h4>

			<div class="calculator-instruction">
				<p>
				The Sound Property Cashflow Calculator has been designed in four steps broken down below.
				For a full introduction and instructional
				video, <a class="text-danger" href="http://soundproperty.com.au/education/cashflow/investment-property-cashflow-calculator/" target="_blank">please refer to this article</a>
				</p>

				<p>
					<span class="bold-text" style="display: block; text-decoration: underline">Step 1: Purchase Costs</span>
				This step captures the capital related costs of the property such as the purchase price,
				deposit and other initial costs. It calculates the stamp duty payable based on the Suburb +
				State entered. The user also has the ability to select 'Cash' or 'Equity' for the purchase costs
				if they are borrowing equity from another property.  If 'Equity' is selected, the calculator
				will apply an interest charge to all purchase costs in the calculation.
				</p>

				<p>
					<span class="bold-text" style="display: block; text-decoration: underline">Step 2: Income and Expenses</span>
				This step requires the weekly income (rent) and various yearly expenses to be entered.
				Also, in this step are any interest or principal repayments based on whether the loan is
				'Interest Only' or 'Principal & Interest'. With 'Principal & Interest', the loan amount is
				repaid over a selected loan term. ‘Interest Only’ loans don’t pay down any of the loan,
				however will result in a better overall cashflow position as no principal repayment is made.
				</p>

				<p>
					<span class="bold-text" style="display: block; text-decoration: underline">Step 3: Tax Factors</span>
				This step captures the yearly income or salary of the user and calculates the applicable ATO
				marginal tax rate. It also estimates the depreciation amount, or the tax deduction for the
				wear and tear over time on the property. The depreciation calculation comes courtesy of
				Washington Brown Quantity Surveyors.
				</p>

				<p>
					<span class="bold-text" style="display: block; text-decoration: underline">Step 4: Cashflow Estimates</span>
				The final step of the calculator reveals the first-year results of the data entered.
				These calculations show the relationship between the property’s rental income and expenses,
				with and without tax implications (i.e. post-tax and pre-tax). The results are displayed
				in weekly and annual amounts. This step also includes an equity growth table where the user
				can enter a chosen average capital growth figure and see what the compounding effect
				of this growth is over a 10-year period. It also shows the remaining loan balance and
				subsequent equity the user may accumulate over this time.
				</p>

				<p>
				The steps are designed to work through in order however can be navigated backwards
				to change any input. There are various help features throughout the calculator symbolised - <span class="bold-text">(?)</span>
				</p>

				<span class="bold-text" style="display: block; text-decoration: underline">
					DISCLAIMER: Sound Property Group accepts no responsibility for the accuracy of data entered
					into the Sound Property Cashflow Calculator, and its subsequent estimates. Results are not
					financial advice and are a general guide only. Always consult a qualified and licensed
					accountant or financial professional before making any decisions to invest. Please refer to
					the full <a class="text-danger" href="https://soundproperty.com.au/cashflow-calculator-privacy-policy-disclaimer/" target="_blank">disclaimer</a>
					before using Sound Property Cashflow Calculator.
				</span>
			</div>
		</div>
	</div>
</template>

<script>
	import PurchaseCosts from './PurchaseCosts';
	import IncomeAndExpenses from './IncomeAndExpenses';
	import TaxFactors from './TaxFactors';
	import Results from './Results';
	import StepButton from './StepButton';
	import PreviousStepButton from './PreviousStepButton';
	import NextStepButton from './NextStepButton';
	import HelpModal from './HelpModal';
    import VueRouter from 'vue-router';

export default {
    components: {
        PurchaseCosts,
        IncomeAndExpenses,
        TaxFactors,
        Results,
        StepButton,
        PreviousStepButton,
        NextStepButton,
		HelpModal
	},
    name: 'cashflow-calculator',
  	data () {
    	return {
        	stepTitles: ['Purchase Costs', 'Income and Expenses', 'Tax Factors', 'Results'],
			currentStepIndex: 0,
			showHelpModal: false
    	}
  	},
	methods: {
        closeHelpModal: function() {
            this.showHelpModal = false;
        },
        previousStepChange: function () {
            var stepBack = '';
            if (this.currentStepIndex == 1) {
                stepBack = 'Step2-Back';
            } else if (this.currentStepIndex == 2) {
                stepBack = 'Step3-Back';
            } else if (this.currentStepIndex == 3) {
                stepBack = 'Step4-Back';
            }

			if (typeof gtag === 'function') {
                gtag('event', 'Click', {
                    'event_category': 'CashflowCalculator',
                    'event_label': stepBack});
			}

            this.currentStepIndex--;
        },
        nextStepChange: function () {
            var stepNext = '';
            var page = '';
            if (this.currentStepIndex == 0) {
                stepNext = 'Step1-Next';
                page = '/calculator/step2';
            } else if (this.currentStepIndex == 1) {
                stepNext = 'Step2-Next';
                page = '/calculator/step3';
            } else if (this.currentStepIndex == 2) {
                stepNext = 'Step3-Next';
                if (this.$store.state.isModalVisible) {
                    page = '/calculator/step4-hidden';
				} else {
                    page = '/calculator/step4-converted';
				}
            }

            if (typeof gtag === 'function') {
                gtag('event', 'Click', {
                    'event_category': 'CashflowCalculator',
                    'event_label': stepNext});
                gtag('config', 'UA-50707122-1', {'page_path': page});
            }

            var propertySuburb = this.$store.state.purchaseCosts.getPropertySuburb();
            var purchasePrice = this.$store.state.purchaseCosts.getPurchasePrice();
            var weeklyRentalIncome = this.$store.state.incomeAndExpenses.getRentalIncomeAmount();
            var interestRate = this.$store.state.incomeAndExpenses.getInterestRate();

            var previousStepIndex = this.currentStepIndex;
            if (!(this.currentStepIndex === 0 && (propertySuburb == '' || purchasePrice === 0)) &&
                !(this.currentStepIndex === 1 && (weeklyRentalIncome === 0 || interestRate === 0))) {
                this.currentStepIndex++;
            }

            if (this.currentStepIndex === 0) {
                var textfieldInput = document.getElementsByClassName('input')[0];
                if (propertySuburb == '') {
                    this.$store.commit('propertySuburbEntered', false);
                    if (!this.$store.state.propertySuburbEntered) {
                        textfieldInput.classList.add("not-entered-input");
                    } else {
                        textfieldInput.classList.remove("not-entered-input");
                    }
                } else {
                    this.$store.commit('propertySuburbEntered', true);
                    textfieldInput.classList.remove("not-entered-input");
                }
                if (purchasePrice === 0) {
                    this.$store.commit('purchasePriceEntered', false);
                } else {
                    this.$store.commit('purchasePriceEntered', true);
                }
			}

            if (this.currentStepIndex === 1 && previousStepIndex === this.currentStepIndex) {
                if (weeklyRentalIncome === 0) {
                    this.$store.commit('weeklyRentalIncomeEntered', false);
                } else {
                    this.$store.commit('weeklyRentalIncomeEntered', true);
                }
                if (interestRate === 0) {
                    this.$store.commit('interestRateEntered', false);
                } else {
                    this.$store.commit('interestRateEntered', true);
                }
            }
        },
		viewToTop: function () {
			var cashflowCalculator = this.$refs['cashflow-calculator'];
            cashflowCalculator.scrollIntoView(true);

            var scrolledY = window.scrollY;

            if (scrolledY) {
                window.scroll(0, scrolledY - 128);
            }
        }
	},
	computed: {
        helpModalVisibility () {
			return this.showHelpModal;
        }
	},
	beforeCreate() {
        this.$store.subscribe((mutation, state) => {
            localStorage.setItem('soundpropertycalculator', JSON.stringify(state));
        });

        this.$store.commit('initialiseStore');

	},
	created() {
        if (this.$route.query['wants-contact'] == "true") {
            this.currentStepIndex = 3;

            if (this.$store.state.isModalVisible) {
                if ('' !== this.$route.query['phone'].trim()) {
                    this.$store.commit('wantsContact', true);
				}
                this.$store.commit('email', this.$route.query['email']);
                this.$store.commit('phoneNumber', this.$route.query['phone']);
                this.$store.commit('name', this.$route.query['user-name']);
                this.$store.commit('modalVisibility', false)
			} else if (!this.$store.state.wantsContact && '' !== this.$route.query['phone'].trim()) {
				this.$store.commit('wantsContact', true);
			}
        }
	},
	mounted() {
        var textfieldInput = document.getElementsByClassName('input')[0];
        let $vm = this;
        textfieldInput.addEventListener('focus', function () {
            $vm.$store.commit('propertySuburbEntered', true);
            textfieldInput.classList.remove("not-entered-input");
        });
	}
}
</script>

<style>

	#cashflow-calculator {
		background: rgb(246, 247, 248);
		overflow: auto;
	}

	#print-logo {
		display: none;
		filter: brightness(50%);
		margin: 0 auto;
		height: 150px;
	}

	.calculator-steps {
		overflow: auto;
		background: rgb(246, 247, 248);
	}

	.calculator-info {
		width: 85%;
		margin: 15px auto;
		background: rgb(246, 247, 248);
		color: #465978;
	}

	.calculator-description {
		text-align: left;
	}

	.step-enter-active, .step-leave-active {
		transition: opacity .3s ease;
	}

	.step-enter-active {
		transition-delay: 0.5s;
	}
	.step-enter, .step-leave-to /* .list-leave-active below version 2.1.8 */ {
		opacity: 0;
		/*transform: translateY(30px);*/
	}

    #previous-step-button {
        float: left;
        margin-left: 4% !important;
		display: none;
    }

    #next-step-button {
        float: right;
        margin-right: 4% !important;
		display: none;
	}

	.bold-text {
		font-weight: bold;
	}

	@media not print {
		@media (min-width: 1024px) {

			.calculator-steps {
				width: 70%;
				min-width: 560px;
				max-width: 1100px;
				margin: 0 auto;
			}

			.calculator-info {
				width: 90%;
				clear: both;
				margin-top: 100px;
				margin-bottom: 50px;
			}

			.calculator-description {
				text-align: left;
			}

			#step-button {
				display: none;
			}

			#previous-step-button {
				display: block;
			}

			#next-step-button {
				display: block;
			}
		}
	}

	@media print {
		html, body {
			height: 100% !important;
			width: 100% !important;
			margin: 0 !important;
		}

		body {
			margin: 0;
			padding: 0;
			-webkit-text-size-adjust: 100% !important;
			-ms-text-size-adjust: 100% !important;
			-webkit-font-smoothing: antialiased !important;
		}

		body {
			display: block !important;
			overflow: visible !important;
		}

		#cashflow-calculator {
			overflow: visible;
			background-color: transparent;
		}

		.calculator-steps {
			overflow: visible;
			background-color: transparent;
		}

		@page { margin:5mm; }

		.show-print {
			display: block !important;
		}

		.hide-print {
			display: none !important;
		}
	}

	@import '../../node_modules/bootstrap/dist/css/bootstrap.css';
</style>


// WEBPACK FOOTER //
// src/Components/CashflowCalculator.vue